import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Skeleton from '../common/Skeleton';
import { Save, Check, CircleMinus, CircleCheck, Lock, Edit2 } from 'lucide-react';
import riskCategoriesService, { RiskCategory } from '../../services/riskCategoriesService';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission } from '../../contexts/PermissionContext';
import { RBAC_ACTIVITY } from './rbacMock';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { isUniqueLocal } from '../../validation/uniqueness';

interface RiskCategoriesTabProps {
  createTrigger?: number;
  pageSize: number;
  onPageSizeChange?: (size: number) => void;
  searchTerm: string;
  statusFilter: string;
}

// workflow_state comes from the backend response after
// migration 101_rsk_masters_workflow.sql.
type WorkflowRecord = { workflow_state: 'draft' | 'submitted' | 'approved' | 'rejected' };
type RowWithWorkflow = RiskCategory & WorkflowRecord;

const RiskCategoriesTab: React.FC<RiskCategoriesTabProps> = ({ createTrigger, pageSize, onPageSizeChange, searchTerm, statusFilter }) => {
  // ── RBAC gates ──────────────────────────────────────────────────────────
  const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
  const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed;
  const permsLoading = viewPerm.loading;
  // Note: approve/reject/reopen gating is resolved by the WF engine inside
  // <WorkflowModal> (it returns per-action permissions), so no canApprove here.

  const [categories, setCategories] = useState<RowWithWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<RowWithWorkflow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // View-first edit: existing records open read-only; "Edit" enables the form.
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<RiskCategory>>({});
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const fieldVh = useFieldValidation();

  // ── Workflow modal state ────────────────────────────────────────────────
  const [workflowOpenFor, setWorkflowOpenFor] = useState<RowWithWorkflow | null>(null);

  // ── Audit history modal state ───────────────────────────────────────────

  const loadCategories = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await riskCategoriesService.getAll({
        status: statusFilter as any,
        search: searchTerm || undefined,
        limit: 100
      });
      const sorted = [...data].sort((a, b) => (b.id || 0) - (a.id || 0));
      setCategories(sorted as RowWithWorkflow[]);
    } catch (err: any) {
      setToast({ type: 'error', message: err.message || 'Failed to load risk categories' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, canView]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);
  useEffect(() => { if (createTrigger && canModify) handleAddNew(); }, [createTrigger]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSubmitting(false);
    setToggling(false);
  }, [showModal]);

  // ── Row click → open modal (read-only if not modify, locked if approved) ─
  const handleRowClick = (category: RowWithWorkflow) => {
    setSelectedCategory(category);
    setFormData(category);
    fieldVh.reset();
    setIsEditMode(true);
    setIsEditing(false); // open read-only; "Edit" enables the form
    setShowModal(true);
  };

  const handleAddNew = () => {
    if (!canModify) return;
    setSelectedCategory(null);
    setFormData({ category_name: '', description: '', is_active: 1 });
    fieldVh.reset();
    setIsEditMode(false);
    setIsEditing(true); // create is always editable
    setShowModal(true);
  };

  const isApproved = selectedCategory?.workflow_state === 'approved';
  const fieldsDisabled = isEditMode && (!isEditing || !canModify || isApproved);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fieldsDisabled) return;
    const ok = fieldVh.validateAll([
      { name: 'category_name', code: 109, value: formData.category_name || '', required: true },
      { name: 'description', code: 110, value: formData.description || '' },
    ]);
    if (!ok) return;
    if (!isUniqueLocal(917, (formData.category_name || ''), categories, selectedCategory?.id)) {
      fieldVh.setError('category_name', 'Category with this name already exists');
      return;
    }
    setSubmitting(true);
    try {
      if (isEditMode && selectedCategory) {
        const { is_active: _ia, ...updateData } = formData;
        await riskCategoriesService.update(selectedCategory.id, updateData);
        setToast({ type: 'success', message: 'Risk Category updated successfully.' });
      } else {
        await riskCategoriesService.create(formData);
        setToast({ type: 'success', message: 'Risk Category created successfully.' });
      }
      setShowModal(false);
      loadCategories();
    } catch (err: any) {
      const msg = extractApiError(err, 'Operation failed');
      if (/exist|duplicate|unique/i.test(msg)) {
        fieldVh.setError('category_name', msg);
      } else {
        setToast({ type: 'error', message: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedCategory || !canModify || isApproved) return;
    const isActive = !!selectedCategory.is_active;
    const next = isActive ? 0 : 1;
    const confirmFn = isActive ? confirmWarning : confirmActive;
    const ok = await confirmFn({
      title: next ? 'Reactivate this category?' : 'Inactivate this category?',
      text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
      confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
    });
    if (!ok) return;
    setToggling(true);
    try {
      await riskCategoriesService.toggleStatus(selectedCategory.id);
      setShowModal(false);
      setToast({ type: next ? 'success' : 'warning', message: next ? 'Category reactivated.' : 'Category inactivated.' });
      loadCategories();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to update status') });
    } finally {
      setToggling(false);
    }
  };

  // Workflow (submit/approve/reject/reopen) is now driven end-to-end by the
  // unified <WorkflowModal> via the /api/v1/wf engine (kind="rsk_category").

  const totalRecords = categories.length;
  const paginatedRows = useMemo(
    () => categories.slice((page - 1) * pageSize, page * pageSize),
    [categories, page, pageSize],
  );

  return (
    <>
      <div className="scroll-container flex-1 overflow-hidden">
        {!permsLoading && !canView ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <NoDataFound
              title="Not authorised"
              message={viewPerm.message || 'You are not authorised to view Risk Masters. Please contact your Super Admin.'}
            />
          </div>
        ) : (
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th table-list-cell--sno">Code</th>
                    <th className="table-list-th">Category Name</th>
                    <th className="table-list-th">Description</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && <Skeleton.TableRows cols={['3xs', '3xs', '3xs', 'md', '3xs']} />}
                  {!loading && categories.length === 0 && (
                    <tr>
                      <td colSpan={5} className="table-list-cell" style={{ padding: '40px 20px', textAlign: 'center' }}>
                        <NoDataFound
                          title="No Risk Categories Found"
                          message={canModify ? 'Click "Create" to add one.' : 'No records to display.'}
                        />
                      </td>
                    </tr>
                  )}
                  {!loading && paginatedRows.map((category, idx) => (
                    <tr
                      key={category.id}
                      className={`table-list-row${category.is_active === 1 ? '' : ' is-inactive'}`}
                      data-testid={`BTN_risk_cat_row_${category.id}_open`}
                      onClick={() => handleRowClick(category)}
                    >
                      <td
                        className="table-list-cell table-list-cell--dot"
                        onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(category); }}
                        title="Workflow & history"
                        data-testid={`BTN_risk_cat_row_${category.id}_workflow`}
                      >
                        <ApprovalDot
                          state={category.workflow_state}
                          onClick={e => { e.stopPropagation(); setWorkflowOpenFor(category); }}
                          title="Workflow & history"
                        />
                      </td>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell table-list-cell--sno"><span className="cell-code">{category.code ?? '—'}</span></td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title cell-name-lock">
                          {category.workflow_state === 'approved' && (
                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                          )}
                          {category.category_name}
                        </span>
                      </td>
                      <td className="table-list-cell table-list-cell--truncate" title={category.description || ''}>
                        {category.description || <span className="text-gray-400 italic">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={onPageSizeChange}
              recordLabel="categories"
            />
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} size="md" data-testid="MOD_risk_cat_form">
        <Modal.Header>
          {isEditMode && selectedCategory
            ? <>{isApproved ? 'Viewing (locked)' : (canModify && isEditing) ? 'Editing' : 'Viewing'} "<span className="item-highlight">{selectedCategory.category_name}</span>"</>
            : 'Create Risk Category'}
        </Modal.Header>
        <Modal.Body>
          {isApproved && (
            <div className="approved-banner">
              <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
            </div>
          )}
          <form id="category-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            {/* System code — read-only, prefilled with the same code the list shows. */}
            {isEditMode && (
              <FormControl.Input
                label="Code"
                value={(selectedCategory as any)?.code || '—'}
                disabled
                data-testid="INP_risk_cat_code"
              />
            )}
            <ValidatedInput
              vh={fieldVh}
              name="category_name"
              code={109}
              required
              label="Category Name"
              type="text"
              data-testid="INP_risk_cat_name"
              value={formData.category_name || ''}
              onChange={e => {
                setFormData({ ...formData, category_name: e.target.value });
              }}
              placeholder="e.g., Operational Risk"
              disabled={fieldsDisabled}
            />
            <ValidatedTextarea
              vh={fieldVh}
              name="description"
              code={110}
              label="Description"
              data-testid="INP_risk_cat_description"
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Describe this risk category…"
              disabled={fieldsDisabled}
            />
          </form>
        </Modal.Body>
        <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
          {isEditMode && selectedCategory && canModify && !isApproved && (
            <Button
              variant={selectedCategory.is_active ? 'inactive-outline' : 'active-outline'}
              icon={selectedCategory.is_active ? CircleMinus : CircleCheck}
              data-testid="BTN_risk_cat_toggle_active"
              onClick={handleToggleActive}
              disabled={toggling || submitting}
            >
              {toggling ? '...' : selectedCategory.is_active ? 'Inactive' : 'Active'}
            </Button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Create → Submit. */}
            {!isEditMode && (
              <Button
                type="submit"
                form="category-form"
                variant="fill"
                icon={Save}
                data-testid="BTN_risk_cat_submit"
                disabled={submitting || toggling}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
            )}
            {/* View mode → "Edit" enables the form (not for approved/locked). */}
            {isEditMode && canModify && !isApproved && !isEditing && (
              <Button
                variant="fill"
                icon={Edit2}
                data-testid="BTN_risk_cat_edit"
                onClick={() => setIsEditing(true)}
                disabled={submitting || toggling}
              >
                Edit
              </Button>
            )}
            {/* Edit mode → "Update" saves. */}
            {isEditMode && canModify && !isApproved && isEditing && (
              <Button
                type="submit"
                form="category-form"
                variant="update"
                icon={Check}
                data-testid="BTN_risk_cat_update"
                disabled={submitting || toggling}
              >
                {submitting ? 'Updating...' : 'Update'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <WorkflowModal
        isOpen={!!workflowOpenFor}
        onClose={() => setWorkflowOpenFor(null)}
        kind="rsk_category"
        id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
        onChanged={loadCategories}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default RiskCategoriesTab;
